import { fetchConfig } from '@theme/utilities';

/**
 * Intercepts checkout and asks about food allergies before redirecting.
 */
class AllergyCheckoutModal extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {HTMLTextAreaElement | null} */
  #textarea = null;

  /** @type {HTMLButtonElement | null} */
  #continueButton = null;

  /** @type {HTMLButtonElement | null} */
  #noneButton = null;

  /** @type {boolean} */
  #submitting = false;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#textarea = this.querySelector('[data-allergy-input]');
    this.#continueButton = this.querySelector('[data-allergy-continue]');
    this.#noneButton = this.querySelector('[data-allergy-none]');

    document.addEventListener('click', this.#onCheckoutClick, true);
    document.addEventListener('submit', this.#onCartSubmit, true);
    this.#continueButton?.addEventListener('click', this.#onContinue);
    this.#noneButton?.addEventListener('click', this.#onNone);
    this.querySelector('[data-allergy-close]')?.addEventListener('click', this.#onClose);
    this.#dialog?.addEventListener('cancel', this.#onClose);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onCheckoutClick, true);
    document.removeEventListener('submit', this.#onCartSubmit, true);
  }

  /**
   * @param {MouseEvent} event
   */
  #onCheckoutClick = (event) => {
    if (this.#submitting) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('#checkout, .cart__checkout-button, [name="checkout"]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    if (button.closest('allergy-checkout-modal')) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.#open();
  };

  /**
   * @param {SubmitEvent} event
   */
  #onCartSubmit = (event) => {
    if (this.#submitting) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

    const submitter = event.submitter;
    if (!(submitter instanceof HTMLButtonElement)) return;
    if (
      submitter.name !== 'checkout' &&
      submitter.id !== 'checkout' &&
      !submitter.classList.contains('cart__checkout-button')
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.#open();
  };

  #open = () => {
    if (!this.#dialog) return;

    if (this.#dialog.open) return;
    this.#dialog.showModal();
    this.#textarea?.focus();
  };

  #onClose = (event) => {
    event?.preventDefault?.();
    this.#dialog?.close();
  };

  #onContinue = async () => {
    const value = this.#textarea?.value?.trim() || '';
    const note = value ? `Alergias: ${value}` : 'Alergias: Nenhuma';
    await this.#saveAndCheckout(note);
  };

  #onNone = async () => {
    if (this.#textarea) this.#textarea.value = '';
    await this.#saveAndCheckout('Alergias: Nenhuma');
  };

  /**
   * @param {string} note
   */
  #saveAndCheckout = async (note) => {
    if (this.#submitting) return;
    this.#submitting = true;
    this.#setBusy(true);

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ note }),
      });

      const response = await fetch(Theme.routes.cart_update_url, config);
      if (!response.ok) throw new Error('Failed to update cart note');

      window.location.href = '/checkout';
    } catch (error) {
      console.error(error);
      this.#submitting = false;
      this.#setBusy(false);
      alert('Não foi possível salvar a informação de alergia. Tente novamente.');
    }
  };

  /**
   * @param {boolean} busy
   */
  #setBusy = (busy) => {
    if (this.#continueButton) this.#continueButton.disabled = busy;
    if (this.#noneButton) this.#noneButton.disabled = busy;
    if (this.#textarea) this.#textarea.disabled = busy;
    this.toggleAttribute('aria-busy', busy);
  };
}

if (!customElements.get('allergy-checkout-modal')) {
  customElements.define('allergy-checkout-modal', AllergyCheckoutModal);
}
